import { useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Typography } from '@material-ui/core';
import { useDropzone } from 'react-dropzone';
import PublishOutlinedIcon from '@material-ui/icons/PublishOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import { AnswerValue } from './trackRecordV2.config';

// sectionProcess values used by BorrowerTrackRecordVerificationStore.uploadLicenseFile.
const LICENSE_SECTION_PROCESS: Record<string, string> = {
  generalContractor: 'BORROWER_GC_LICENSE',
  broker: 'BORROWER_BROKER_LICENSE',
};

// generalContractorUploads/brokerUploads rows: { taskId, name, fileExtension, ... }.
const LICENSE_UPLOADS_FIELD: Record<string, string> = {
  generalContractor: 'generalContractorUploads',
  broker: 'brokerUploads',
};

export const QuestionContent = observer(({
  question,
  answers,
  onAnswer,
  classes,
  verificationStore,
  documentStore,
  borrowerId,
}: {
  question: { key: string; label: string; uploadLabel?: string };
  answers: Record<string, AnswerValue>;
  onAnswer: (key: string, value: AnswerValue) => void;
  classes: Record<string, string>;
  verificationStore?: any;
  documentStore?: any;
  borrowerId?: number | string;
}) => {
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const sectionProcess = LICENSE_SECTION_PROCESS[question.key];

  const onDropLicenseFile = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file || !verificationStore || !sectionProcess) return;

    const fresh = { name: file.name, file, type: file.name.split('.').pop() };
    const existingTaskId =
      question.key === 'generalContractor' ? verificationStore.gcLicenseTaskId : verificationStore.brokerLicenseTaskId;

    setUploadedFileName(file.name);
    setIsUploading(true);
    try {
      // enqueueForDocReview=true: this new V2 flow queues the license for doc-review automation.
      const res = await verificationStore.uploadLicenseFile(sectionProcess, fresh, existingTaskId, documentStore, true);
      if (!res?.taskId) {
        setUploadedFileName(null);
      }
    } catch (error) {
      console.error('License upload failed:', error);
      setUploadedFileName(null);
      verificationStore.globalStore?.notificationStore?.showErrorNotification?.({
        message: 'Error occurred while uploading the license',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps: getLicenseRootProps, getInputProps: getLicenseInputProps, isDragActive: isLicenseDragActive } =
    useDropzone({ onDrop: onDropLicenseFile, multiple: false, maxFiles: 1 });

  const existingUploads = verificationStore?.[LICENSE_UPLOADS_FIELD[question.key]] ?? [];

  const handleDownloadExisting = (upload: any) => {
    if (documentStore?.downloadDocument) {
      documentStore.downloadDocument({
        taskId: upload.taskId,
        originalFileName: upload.name,
        fileExtension: upload.fileExtension,
        taskName: upload.name,
      });
    } else {
      verificationStore?.downloadTaskFile?.(upload.taskId, upload.name, upload.fileExtension);
    }
  };

  return (
    <>
      <Box className={classes.questionRow}>
        <Typography className={classes.questionLabel}>{question.label}</Typography>
        <Box className={classes.answerGroup}>
          {(['Yes', 'No'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={`${classes.answerButton} ${
                answers[question.key] === option ? classes.answerButtonSelected : ''
              }`}
              onClick={() => onAnswer(question.key, option)}
            >
              {option}
            </button>
          ))}
        </Box>
      </Box>
      {question.uploadLabel && answers[question.key] === 'Yes' && (
        <Box className={classes.uploadArea} {...getLicenseRootProps()}>
          <input {...getLicenseInputProps()} />
          <PublishOutlinedIcon className={classes.uploadIcon} />
          <Typography className={classes.uploadText}>
            {isUploading ? (
              'Uploading...'
            ) : isLicenseDragActive ? (
              'Drop the file here...'
            ) : uploadedFileName ? (
              uploadedFileName
            ) : (
              <>
                {question.uploadLabel} — <span className={classes.uploadLink}>drag &amp; drop or click to upload</span>
              </>
            )}
          </Typography>
        </Box>
      )}
      {question.uploadLabel && answers[question.key] === 'Yes' && existingUploads.length > 0 && (
        <Box style={{ marginTop: 8 }}>
          {existingUploads.map((upload: any, index: number) => (
            <Box
              key={upload.taskId ?? index}
              className={classes.uploadLink}
              style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
              onClick={() => handleDownloadExisting(upload)}
            >
              <GetAppIcon style={{ fontSize: 14 }} />
              <Typography style={{ color: 'inherit', fontSize: 13 }} noWrap>
                {upload.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
});
